import React from 'react';
import { HERITAGE_ASSETS } from '@/lib/api';
import { Panel, SectionDivider } from '@/components/Panel';
import { HeritageSeal, DahliaMark } from '@/components/BrandMark';
import {
  Feather, Waves, Sparkles, Shirt, Wind, Sun,
  Move, Ruler, Home, Heart, Shield, Layers, Anchor,
} from 'lucide-react';

const PANTS_SIZES = [
  {
    label: 'Small',
    fits: 'Fits XS to Small',
    rows: [
      ['Waist (Relaxed)', '62 cm'],
      ['Comfortable Stretch', '62–82 cm'],
      ['Maximum Stretch', 'Up to 90 cm'],
      ['Hip Capacity', '102–107 cm'],
      ['Inseam', '75 cm'],
    ],
    recommended: ["NZ Women's 8 to 10", 'XS to Small'],
  },
  {
    label: 'Medium',
    fits: 'Fits Small to Large',
    rows: [
      ['Waist (Relaxed)', '72 cm'],
      ['Comfortable Stretch', '72–94 cm'],
      ['Maximum Stretch', 'Up to 102 cm'],
      ['Hip Capacity', '107–117 cm'],
      ['Inseam', '75 cm'],
    ],
    recommended: ["NZ Women's 10 to 14", 'Small to Large'],
  },
  {
    label: 'Large',
    fits: 'Fits Large to XL',
    rows: [
      ['Waist (Relaxed)', '82 cm'],
      ['Comfortable Stretch', '82–108 cm'],
      ['Maximum Stretch', 'Up to 118 cm'],
      ['Hip Capacity', '117–137 cm'],
      ['Inseam', '76 cm'],
    ],
    recommended: ["NZ Women's 16 to 22", 'Large to XL'],
  },
];

const FEATURES = [
  { icon: Layers, label: 'Heavyweight premium cotton blend' },
  { icon: Feather, label: 'Ultra soft brushed fleece interior' },
  { icon: Move, label: 'Four way comfort stretch' },
  { icon: Shield, label: 'Pre shrunk for consistent sizing' },
  { icon: Wind, label: 'Breathable' },
  { icon: Sun, label: 'Warm without excess bulk' },
  { icon: Shirt, label: 'Oversized relaxed silhouette' },
  { icon: Anchor, label: 'Shape retaining elastic waistband' },
  { icon: Home, label: 'Made for the farm, home and town' },
  { icon: Heart, label: 'Designed for everyday comfort' },
];

export default function SizeGuidePage() {
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14 overflow-x-hidden" data-testid="size-guide-page">
      {/* Title */}
      <div className="text-center">
        <HeritageSeal size={84} />
        <div className="h-eyebrow mt-4">Heritage Apparel</div>
        <h1 className="h-display text-5xl md:text-7xl mt-2" style={{ color: 'var(--heritage-plum)' }}>Size Guide</h1>
        <div className="flex items-center justify-center gap-4 mt-4">
          <div className="h-px flex-1 max-w-[120px]" style={{ background: 'rgba(75,24,56,0.35)' }} />
          <DahliaMark size={22} />
          <div className="h-px flex-1 max-w-[120px]" style={{ background: 'rgba(75,24,56,0.35)' }} />
        </div>
      </div>

      {/* Designed to be oversized + Comparison */}
      <div className="grid lg:grid-cols-2 gap-5 sm:gap-6 mt-10">
        <Panel className="p-5 sm:p-8">
          <div className="h-eyebrow">Designed to be oversized</div>
          <div className="h-serif text-2xl sm:text-3xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Intentionally relaxed. Made to move.</div>
          <p className="text-sm opacity-85 mt-3 leading-relaxed" style={{ color: 'var(--heritage-plum)' }}>
            Every Heritage Apparel garment has been intentionally designed with a relaxed oversized fit. Choose your usual Heritage size for the intended oversized look, or size down for a closer fit.
          </p>
          <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm" style={{ color: 'var(--heritage-plum)' }}>
            {[
              'Relaxed oversized silhouette',
              'Premium heavyweight cotton blend',
              'Soft brushed fleece interior',
              'Extra large crossover hood',
              'Dropped shoulders',
              'Wide ribbed waistband',
              'Ribbed cuffs',
              'Relaxed leg profile',
              'Premium elastic waistband',
              'Four way comfort stretch',
            ].map((l) => (
              <li key={l} className="flex gap-2"><span aria-hidden>◈</span><span>{l}</span></li>
            ))}
          </ul>
        </Panel>

        <Panel className="p-5 sm:p-8">
          <div className="h-eyebrow">Heritage size comparison</div>
          <div className="h-serif text-2xl sm:text-3xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Three sizes. Broad range.</div>
          <div className="mt-4 rounded-xl overflow-hidden" style={{ border: '1.5px solid rgba(75,24,56,0.3)' }}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: 'rgba(75,24,56,0.08)' }}>
                    <th className="text-left py-2.5 px-2 sm:px-3 h-eyebrow whitespace-nowrap" style={{ letterSpacing: '0.18em' }}>Heritage</th>
                    <th className="text-left py-2.5 px-2 sm:px-3 h-eyebrow whitespace-nowrap" style={{ letterSpacing: '0.18em' }}>Letters</th>
                    <th className="text-left py-2.5 px-2 sm:px-3 h-eyebrow whitespace-nowrap" style={{ letterSpacing: '0.18em' }}>NZ&nbsp;Women&#39;s</th>
                  </tr>
                </thead>
                <tbody style={{ color: 'var(--heritage-plum)' }}>
                  {[
                    ['Small', 'XS – S', '8 – 10'],
                    ['Medium', 'S – L', '10 – 14'],
                    ['Large', 'L – XL', '16 – 22'],
                  ].map((row) => (
                    <tr key={row[0]} className="border-t" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
                      <td className="py-2.5 px-2 sm:px-3 h-serif text-base whitespace-nowrap">{row[0]}</td>
                      <td className="py-2.5 px-2 sm:px-3 whitespace-nowrap">{row[1]}</td>
                      <td className="py-2.5 px-2 sm:px-3 whitespace-nowrap">{row[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-xs opacity-75 mt-4" style={{ color: 'var(--heritage-plum)' }}>
            Rather than six or seven incremental sizes, Heritage uses three oversized fits designed to comfortably suit a broad range of body shapes.
          </p>
        </Panel>
      </div>

      <SectionDivider label="Signature Pants Measurements" />

      {/* Pants sizes 3 columns */}
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5 sm:gap-6">
        {PANTS_SIZES.map((s) => (
          <Panel key={s.label} className="p-5 sm:p-6" data-testid={`size-pants-${s.label.toLowerCase()}`}>
            <div className="text-center">
              <div className="h-display text-3xl" style={{ color: 'var(--heritage-plum)' }}>{s.label}</div>
              <div className="h-eyebrow mt-1">{s.fits}</div>
            </div>
            <div className="mt-5 rounded-lg overflow-hidden" style={{ border: '1px solid rgba(75,24,56,0.2)' }}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <tbody style={{ color: 'var(--heritage-plum)' }}>
                    {s.rows.map((row, i) => (
                      <tr key={row[0]} className={i > 0 ? 'border-t' : ''} style={{ borderColor: 'rgba(75,24,56,0.12)' }}>
                        <td className="py-2 px-2.5 sm:px-3 opacity-80">{row[0]}</td>
                        <td className="py-2 px-2.5 sm:px-3 h-serif text-right whitespace-nowrap">{row[1]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="mt-5 text-center">
              <div className="h-eyebrow">Recommended for</div>
              <div className="h-serif mt-1" style={{ color: 'var(--heritage-plum)' }}>{s.recommended[0]}</div>
              <div className="text-xs opacity-75 mt-0.5" style={{ color: 'var(--heritage-plum)' }}>{s.recommended[1]}</div>
            </div>
          </Panel>
        ))}
      </div>

      <SectionDivider label="Hoodie & Waist Guide" />

      {/* Hoodie + Waist Stretch Guide */}
      <div className="grid lg:grid-cols-2 gap-5 sm:gap-6">
        <Panel className="p-5 sm:p-8">
          <div className="h-eyebrow">Hoodie Fit</div>
          <div className="h-serif text-2xl sm:text-3xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Oversized. Structured.</div>
          <p className="text-sm opacity-85 mt-2 leading-relaxed" style={{ color: 'var(--heritage-plum)' }}>
            Our Heritage Signature Hoodies are intentionally oversized while maintaining a premium structured shape. The waistband gently pulls the garment back into shape, creating an oversized fit without appearing bulky.
          </p>
          <ul className="mt-4 grid grid-cols-1 gap-y-2 text-sm" style={{ color: 'var(--heritage-plum)' }}>
            {[
              'Oversized relaxed body',
              'Dropped shoulders',
              'Extra large crossover hood',
              'Heavyweight premium fleece',
              'Thick ribbed waistband',
              'Ribbed cuffs',
              'Spacious kangaroo pocket',
            ].map((l) => (
              <li key={l} className="flex gap-2"><DahliaMark size={16} /><span>{l}</span></li>
            ))}
          </ul>
        </Panel>

        <Panel className="p-5 sm:p-8">
          <div className="h-eyebrow">Waist Stretch Guide</div>
          <div className="h-serif text-2xl sm:text-3xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Move with your day.</div>
          <p className="text-sm opacity-85 mt-2 leading-relaxed" style={{ color: 'var(--heritage-plum)' }}>
            Our Signature Pants use a premium wide elastic waistband designed to move naturally with your body.
          </p>
          <div className="mt-5 space-y-3">
            {[
              { label: 'Relaxed Waist', copy: 'Measured laying flat with no stretch applied.', tone: 0.9 },
              { label: 'Comfortable Stretch', copy: 'The recommended everyday range. Secure yet comfortable.', tone: 0.55 },
              { label: 'Maximum Stretch', copy: 'The upper limit of the elastic. Stay within Comfortable Stretch for garment longevity.', tone: 0.25 },
            ].map((r) => (
              <div key={r.label} className="p-3 sm:p-4 rounded-lg flex items-start gap-3 sm:gap-4" style={{ background: `rgba(75,24,56,${r.tone * 0.15})`, border: '1px solid rgba(75,24,56,0.15)' }}>
                <Ruler size={22} style={{ color: 'var(--heritage-plum)' }} className="mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="h-serif text-lg" style={{ color: 'var(--heritage-plum)' }}>{r.label}</div>
                  <div className="text-sm opacity-85" style={{ color: 'var(--heritage-plum)' }}>{r.copy}</div>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <SectionDivider label="Fabric & Performance" />

      {/* Feature icons grid */}
      <Panel className="p-5 sm:p-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 sm:gap-6">
          {FEATURES.map(({ icon: Icon, label }) => (
            <div key={label} className="flex flex-col items-center text-center gap-2">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full flex items-center justify-center"
                   style={{ border: '1.5px solid var(--heritage-plum)', color: 'var(--heritage-plum)' }}>
                <Icon size={20} />
              </div>
              <div className="text-[11px] sm:text-xs h-serif leading-tight" style={{ color: 'var(--heritage-plum)' }}>{label}</div>
            </div>
          ))}
        </div>
      </Panel>

      {/* Materials + Care */}
      <div className="grid md:grid-cols-2 gap-5 sm:gap-6 mt-6">
        <Panel className="p-5 sm:p-8">
          <div className="h-eyebrow">Materials</div>
          <div className="h-serif text-2xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Heavyweight brushed fleece</div>
          <ul className="mt-4 space-y-1.5 text-sm" style={{ color: 'var(--heritage-plum)' }}>
            <li className="flex gap-2"><span aria-hidden>◈</span><span>51% Cotton</span></li>
            <li className="flex gap-2"><span aria-hidden>◈</span><span>49% Polyester</span></li>
            <li className="flex gap-2"><span aria-hidden>◈</span><span>Heavyweight brushed fleece</span></li>
            <li className="flex gap-2"><span aria-hidden>◈</span><span>Pre shrunk</span></li>
            <li className="flex gap-2"><span aria-hidden>◈</span><span>Soft touch finish</span></li>
          </ul>
        </Panel>
        <Panel className="p-5 sm:p-8">
          <div className="h-eyebrow">Care Instructions</div>
          <div className="h-serif text-2xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Made to last a decade</div>
          <ul className="mt-4 space-y-1.5 text-sm" style={{ color: 'var(--heritage-plum)' }}>
            <li className="flex gap-2"><span aria-hidden>◈</span><span>Machine wash cold</span></li>
            <li className="flex gap-2"><span aria-hidden>◈</span><span>Wash with similar colours</span></li>
            <li className="flex gap-2"><span aria-hidden>◈</span><span>Turn inside out before washing</span></li>
            <li className="flex gap-2"><span aria-hidden>◈</span><span>Tumble dry low</span></li>
            <li className="flex gap-2"><span aria-hidden>◈</span><span>Do not bleach</span></li>
            <li className="flex gap-2"><span aria-hidden>◈</span><span>Do not iron directly over embroidery</span></li>
            <li className="flex gap-2"><span aria-hidden>◈</span><span>Do not dry clean</span></li>
          </ul>
        </Panel>
      </div>

      <SectionDivider label="Choosing your size" />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
        {[
          { icon: Heart, title: 'For the Heritage look', copy: 'Choose your usual Heritage size for our signature oversized fit.' },
          { icon: Sparkles, title: 'For a fitted look', copy: 'Size down for a slightly more tailored appearance.' },
          { icon: Waves, title: 'For extra room', copy: 'If you are between sizes or have a broader build, choose the larger size for maximum comfort.' },
        ].map(({ icon: Icon, title, copy }) => (
          <Panel key={title} className="p-5 sm:p-6">
            <div className="w-12 h-12 rounded-full flex items-center justify-center"
                 style={{ border: '1.5px solid var(--heritage-plum)', color: 'var(--heritage-plum)' }}>
              <Icon size={20} />
            </div>
            <div className="h-serif text-2xl mt-3" style={{ color: 'var(--heritage-plum)' }}>{title}</div>
            <div className="text-sm mt-1 opacity-85" style={{ color: 'var(--heritage-plum)' }}>{copy}</div>
          </Panel>
        ))}
      </div>

      {/* Why Heritage fits different */}
      <Panel className="p-6 sm:p-10 mt-10 text-center">
        <DahliaMark size={40} />
        <div className="h-eyebrow mt-3">Why Heritage fits different</div>
        <div className="h-display text-2xl sm:text-3xl md:text-4xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Made for real life. Made to be lived in.</div>
        <p className="max-w-2xl mx-auto text-sm md:text-base opacity-85 mt-3 leading-relaxed" style={{ color: 'var(--heritage-plum)' }}>
          Rather than offering six or seven incremental sizes, Heritage Apparel has been carefully designed around three oversized fits that comfortably suit a broad range of body shapes. The result is a premium relaxed silhouette with fewer sizing worries and exceptional comfort. On the farm. In town. At home. Everyday comfort in every stitch.
        </p>
      </Panel>

      {/* Original sheet reference */}
      <SectionDivider label="Full spec sheet" />
      <Panel className="p-2 sm:p-3">
        <img src={HERITAGE_ASSETS.sizeGuideSheet} alt="Heritage Apparel size guide" className="w-full rounded-xl block" />
      </Panel>
    </div>
  );
}
